/**
 * 顶部 KPI 横条（对齐高保真原型 `#kpibar`）。
 *
 * 原型：紧贴顶栏下方的横条（top:58px; height:76px），
 * 7 张 KPI 用 `.kpi{flex:1; padding:0 18px; border-right:1px solid var(--line)}` 等宽横排，
 * 卡内结构为 `.kl`（标签）/ `.kv`（大数值 + 单位）/ `.kd`（说明）。
 *
 * ⚠️ 文案与卡片顺序由 `buildNationalKpis()` 产出，逐字对照原型 `renderKPI()`。
 * ⚠️ 后端无全局「产出物齐套率」聚合接口 → 该卡显示 `—`，绝不编造。
 *
 * ⭐ P6.29：带有 `item.to` 的 KPI 变为可点击下钻（当前仅「审批积压」→ /approvals）。
 *    其余 KPI 保持纯展示（不设 role/tabIndex，避免误以为可点）。
 *    可下钻卡片补 role="button" + tabIndex=0 + Enter/Space 键盘可达（无障碍最小集）。
 */

import { useNavigate } from 'react-router-dom';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { KpiItem } from './useMapData';

export interface KpiBarProps {
  items: readonly KpiItem[];
}

export default function KpiBar({ items }: KpiBarProps) {
  const navigate = useNavigate();

  return (
    <div className="map-kpibar" aria-label="全国关键指标">
      {items.map((item) => {
        const to = item.to;
        const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(to!);
          }
        };
        return (
          <div
            key={item.key}
            className={`kpi ${item.tone}${to ? ' kpi--link' : ''}`}
            data-kpi-key={item.key}
            {...(to
              ? {
                  role: 'button',
                  tabIndex: 0,
                  'aria-label': `${item.label}，点击查看`,
                  onClick: () => navigate(to),
                  onKeyDown,
                }
              : {})}
          >
            <div className="kl">{item.label}</div>
            <div className="kv">
              {item.value}
              {item.unit ? <small>{item.unit}</small> : null}
            </div>
            <div className="kd">{item.desc}</div>
          </div>
        );
      })}
    </div>
  );
}
